'use client';

import type { ReactNode } from 'react';
import { useLayerTracking } from '@packages/modules/src/Tracking/hooks/useLayerTracking/useLayerTracking';
import { useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { TaxHintFragmentFragmentDoc } from '@packages/gql/generated/shopping/TaxHintFragmentFragmentDoc';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { unmask } from '@packages/gql/src/betterMasking';
import { TextWithDialog } from '@packages/modules/src/TextWithDialog/TextWithDialog.client';
import { taxHintMessages } from './messages';
import { getTaxHintMessage } from './getTaxHintMessage';

/* GraphQL */ `
  fragment TaxHintFragment on Delivery {
    deliverySize
  }
`;

export type TaxHintProps = {
  /**
   * The used delivery type for a given product. Each delivery type will show its own delivery info message.
   * */
  data: FragmentType<typeof TaxHintFragmentFragmentDoc>;
};

/**
 * <p>
 * The <code>TaxHint</code> component displays a delivery info message.
 * </p>
 * */
export const TaxHint = ({ data }: TaxHintProps) => {
  const { formatMessage } = useIntl();
  const { trackViewLayer } = useLayerTracking();
  const {
    product: {
      taxHint: { hideAdditionalDeliveryInfo, modalContent },
    },
  } = useConfig();

  const { deliverySize } = unmask<typeof TaxHintFragmentFragmentDoc>(data);

  const dialogContent =
    deliverySize in modalContent ? (
      <ServiceLayer serviceLayerId={modalContent[deliverySize].link} />
    ) : (
      <>{formatMessage(taxHintMessages.dialogContent)}</>
    );

  const title = formatMessage(getTaxHintMessage('TITLE', deliverySize));

  const handleDialogOpen = () => {
    trackViewLayer(`${title} (Liefertyp: ${deliverySize})`);
  };

  const messageValue = {
    link: (msg: ReactNode[]) =>
      !hideAdditionalDeliveryInfo && (
        <TextWithDialog
          message={msg.join('')}
          dialogTitle={title}
          dialogContent={dialogContent}
          onOpenDialogHandler={handleDialogOpen}
          className="text-color-text-darktransparent contents"
          displayTrackingProps={{ category: 'dv-service-layer-tax-hint' }}
          key={msg.join('')}
        />
      ),
  };

  return (
    /* must be a div because the inner text has a dialog rendered inside and a dialog is not valid in a p */
    <div className="text-size-body-xs text-color-text-darktransparent">
      {formatMessage(getTaxHintMessage('TEXT', deliverySize), messageValue)}
    </div>
  );
};
